import {
  changePlatformAdminPassword,
  getPlatformSession,
  getServiceHealth,
  loginPlatformAdmin,
  logoutPlatformAdmin,
  refreshPlatformTokenPair,
} from '@t4s/api-client'
import type { PlatformSession, ServiceHealth, TokenPair } from '@t4s/api-client/model'
import { unwrap } from './api-error.ts'

/**
 * The four calls the session is made of, and the one the masthead is made of.
 *
 * Every one of them is a **generated** client function (`packages/api-client`), so the
 * request shape, the response shape and every documented status come from the OpenAPI
 * document rather than from anything written here. Nothing in this application ever
 * calls `fetch`.
 *
 * The indirection exists so a unit test can supply an authentication state without a
 * network: the store below takes this port, and `createPlatformAuthGateway()` is its one
 * production implementation. It is the same shape `apps/api` uses for the same reason.
 */
export type PlatformAuthGateway = {
  /** Exchanges a nickname and password for a token pair. Throws `ApiFailure` on refusal. */
  signIn(credentials: { nickname: string; password: string }): Promise<TokenPair>
  /** Exchanges a refresh token for the next pair. Throws `ApiFailure` when it is spent. */
  refresh(refreshToken: string): Promise<TokenPair>
  /**
   * Ends this session **server-side**. Both tokens stop working immediately.
   *
   * The access token is a **parameter** rather than something the transport reads out
   * of the store on its way past. This is the one call made *while the session is being
   * torn down* — the idle timeout revokes and then ends locally — so whether it still
   * carries a credential must not depend on how many `await`s happen to sit between
   * here and the `fetch` that assembles the headers. Passed explicitly, it is a value
   * captured at the call, and inserting an await anywhere downstream cannot silently
   * turn the revocation into an unauthenticated request that is refused and swallowed.
   *
   * The cost of pinning it is that the transport's shared 401 recovery deliberately
   * steps aside for this request (`packages/api-client/src/fetch.ts`) — it repairs the
   * *configured* credential, and replaying an `init` that pins its own would re-send the
   * token just refused. An expired token here therefore throws `ApiFailure` at 401, and
   * rotating the pair and calling again is the caller's job: `PlatformSessionStore`
   * does it, because it is the one holding the refresh token.
   */
  revoke(accessToken: string): Promise<void>
}

export function createPlatformAuthGateway(): PlatformAuthGateway {
  return {
    signIn: (credentials) => unwrap<TokenPair>(loginPlatformAdmin(credentials)),
    refresh: (refreshToken) => unwrap<TokenPair>(refreshPlatformTokenPair({ refreshToken })),
    // An explicit header, which the transport leaves alone: `apiFetch` fills in the
    // configured credential only where the caller left a gap, and leaves a 401 from a
    // request that brought its own credential to the caller to recover from.
    revoke: async (accessToken) => {
      await unwrap<void>(
        logoutPlatformAdmin({ headers: { Authorization: `Bearer ${accessToken}` } }),
      )
    },
  }
}

/**
 * Who the current access token authenticates, **as the database sees it right now**.
 *
 * This is what restoring a session means on a surface that persists nothing: the tokens
 * in memory are presented, and the API answers with the account they belong to. A token
 * carries identity only — role and status are re-read on every authenticated request
 * (docs/DECISIONS.md, "Tokens carry no role and no status").
 */
export function readPlatformIdentity(): Promise<PlatformSession> {
  return unwrap<PlatformSession>(getPlatformSession())
}

/**
 * The liveness probe, which is where the masthead's environment word comes from —
 * docs/ux/PLATFORM-ADMIN.md §5.1 and docs/DECISIONS.md, "The environment label comes from
 * the API, not from the build". It touches no database and needs no token.
 */
export function readServiceHealth(): Promise<ServiceHealth> {
  return unwrap<ServiceHealth>(getServiceHealth())
}

/**
 * Changes **this** administrator's password (`POST /platform/auth/password`, §4.4).
 *
 * Answers 204 with no body, and nothing about the calling session changes: its tokens keep working
 * and no new pair is issued, so there is nothing to store. Every *other* session of the account
 * ends on its next request. A wrong current password is a 400 `password_incorrect`, deliberately
 * not a 401, so the transport does not refresh, replay and end this session over a typo.
 *
 * Neither password is kept here, logged, or placed in a message (**HB4**).
 */
export function changePlatformPassword(passwords: {
  readonly currentPassword: string
  readonly newPassword: string
}): Promise<void> {
  return unwrap<void>(changePlatformAdminPassword(passwords))
}
